import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Checkbox,
  Header,
  Icon,
  Label,
  Loader,
  Message,
  Pagination,
} from 'semantic-ui-react';

import { API, showError, showSuccess } from '../../helpers';
import PageSizeSelectV8, {
  readPageSizeV8,
} from '../../components/PageSizeSelectV8';
import './Notification.css';

const formatTime = (timestamp) => {
  if (!timestamp) return '-';
  return new Date(Number(timestamp) * 1000).toLocaleString();
};

const Notification = () => {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [unread, setUnread] = useState(0);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(() =>
    readPageSizeV8('v8-page-size-notifications', 20)
  );
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await API.get(
        '/api/notifications/v8?page=' +
          page +
          '&page_size=' +
          pageSize +
          '&unread_only=' +
          unreadOnly
      );
      if (!response) return;
      if (!response.data?.success) throw new Error(response.data?.message);
      setItems(response.data.data?.notifications || []);
      setTotal(response.data.data?.total || 0);
      setUnread(response.data.data?.unread || 0);
    } catch (error) {
      showError(error.message || t('notifications.errors.load'));
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, unreadOnly, t]);

  useEffect(() => {
    load();
  }, [load]);

  const markRead = async (item) => {
    if (item.read_at) return;
    const response = await API.post(
      '/api/notifications/v8/' + item.id + '/read'
    );
    if (!response) return;
    if (!response.data?.success) {
      showError(response.data?.message || t('notifications.errors.mark_read'));
      return;
    }
    await load();
  };

  const markAll = async () => {
    const response = await API.post('/api/notifications/v8/read-all');
    if (!response) return;
    if (!response.data?.success) {
      showError(response.data?.message || t('notifications.errors.mark_read'));
      return;
    }
    showSuccess(t('notifications.messages.all_read'));
    await load();
  };

  const eventText = (item, field) => {
    const key = 'notifications.events.' + item.event_code + '.' + field;
    const fallback =
      field === 'title'
        ? item.event_code
        : t('notifications.events.fallback.body');
    return t(key, { ...(item.metadata || {}), defaultValue: fallback });
  };

  return (
    <div className='dashboard-container management-container-v4 notification-page-v8'>
      <div className='notification-toolbar-v8'>
        <div>
          <Header as='h2'>{t('notifications.title')}</Header>
          <span className='notification-muted-v8'>
            {t('notifications.unread_count', { count: unread })}
          </span>
        </div>
        <div className='notification-actions-v8'>
          <Checkbox
            toggle
            label={t('notifications.unread_only')}
            checked={unreadOnly}
            onChange={(_, { checked }) => {
              setPage(1);
              setUnreadOnly(checked);
            }}
          />
          <Button
            icon
            labelPosition='left'
            disabled={!unread}
            onClick={markAll}
          >
            <Icon name='check double' />
            {t('notifications.mark_all')}
          </Button>
        </div>
      </div>

      {loading ? (
        <Loader active inline='centered' />
      ) : items.length === 0 ? (
        <Message info content={t('notifications.empty')} />
      ) : (
        <div className='notification-list-v8'>
          {items.map((item) => (
            <button
              type='button'
              key={item.id}
              className={
                'notification-row-v8 ' +
                (item.read_at
                  ? 'notification-read-v8'
                  : 'notification-unread-v8')
              }
              onClick={() => markRead(item)}
            >
              <span
                className={
                  'notification-icon-v8 notification-' + item.severity + '-v8'
                }
                aria-hidden='true'
              >
                <Icon
                  name={
                    item.severity === 'error'
                      ? 'exclamation circle'
                      : item.severity === 'warning'
                      ? 'warning sign'
                      : 'info circle'
                  }
                />
              </span>
              <span className='notification-copy-v8'>
                <span className='notification-title-v8'>
                  {eventText(item, 'title')}
                  {item.occurrences > 1 ? (
                    <Label basic size='tiny'>
                      {t('notifications.occurrences', {
                        count: item.occurrences,
                      })}
                    </Label>
                  ) : null}
                </span>
                <span className='notification-body-v8'>
                  {eventText(item, 'body')}
                </span>
                <span className='notification-time-v8'>
                  {formatTime(item.last_occurred_at)}
                </span>
              </span>
              {!item.read_at ? (
                <span
                  className='notification-dot-v8'
                  title={t('notifications.unread')}
                />
              ) : null}
            </button>
          ))}
        </div>
      )}

      <div className='list-pagination-v8'>
        <PageSizeSelectV8
          label={t('log.table.page_size')}
          storageKey='v8-page-size-notifications'
          value={pageSize}
          onChange={(nextPageSize) => {
            setPage(1);
            setPageSize(nextPageSize);
          }}
        />
        {total > pageSize ? (
          <Pagination
            activePage={page}
            totalPages={Math.ceil(total / pageSize)}
            onPageChange={(_, { activePage }) => setPage(Number(activePage))}
          />
        ) : null}
      </div>
    </div>
  );
};

export default Notification;
